import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Calendar, Users, ShoppingCart, Mail, BarChart3, Trash2, Pencil, Heart, Receipt, X, ExternalLink, Printer, Camera, Image as ImageIcon, Send, Download } from "lucide-react";
import api, { FILE_BASE, resolveImageUrl, API } from "../lib/api";
import { getToken } from "../lib/auth";
import { isLoggedIn, getRole } from "../lib/auth";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { ReceiptLayout } from "./Receipt";

const TABS = [
  { id: "stats", label: "Översikt", icon: BarChart3 },
  { id: "events", label: "Event", icon: Calendar },
  { id: "photographers", label: "Fotografer", icon: Users },
  { id: "orders", label: "Ordrar", icon: ShoppingCart },
  { id: "leads", label: "Leads", icon: Mail },
  { id: "sponsorship", label: "Sponsringspott", icon: Heart },
];

const emptyEvent = {
  name: "",
  date: "",
  location: "",
  series: "",
  classes: "",
  heats: "",
  cover_image_url: "",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [photographers, setPhotographers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [leads, setLeads] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyEvent);
  const [pot, setPot] = useState(null);
  const [payoutForm, setPayoutForm] = useState({ rider_name: "", rider_bib: "", amount: "", description: "", receipt_url: "" });
  const [orderDetail, setOrderDetail] = useState(null);

  useEffect(() => {
    if (!isLoggedIn() || getRole() !== "admin") {
      navigate("/admin/login");
      return;
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async () => {
    try {
      const [s, e, p, o, l, sp] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/events"),
        api.get("/admin/photographers"),
        api.get("/admin/orders"),
        api.get("/admin/leads"),
        api.get("/admin/sponsorship-pot"),
      ]);
      setStats(s.data);
      setEvents(e.data);
      setPhotographers(p.data);
      setOrders(o.data);
      setLeads(l.data);
      setPot(sp.data);
    } catch {
      // ignored
    }
  };

  const submitPayout = async (e) => {
    e.preventDefault();
    const amt = parseFloat(payoutForm.amount);
    if (isNaN(amt) || amt <= 0) return alert("Ange ett giltigt belopp");
    await api.post("/admin/sponsorship-pot/payout", {
      rider_name: payoutForm.rider_name,
      rider_bib: payoutForm.rider_bib || null,
      amount: amt,
      description: payoutForm.description,
      receipt_url: payoutForm.receipt_url || null,
    });
    setPayoutForm({ rider_name: "", rider_bib: "", amount: "", description: "", receipt_url: "" });
    refresh();
  };

  const openOrder = async (id) => {
    try {
      const r = await api.get(`/admin/orders/${id}`);
      setOrderDetail(r.data);
    } catch (err) {
      alert("Kunde inte hämta orderdetaljer");
    }
  };

  const resendOrderEmail = async (orderId, customerEmail) => {
    if (!window.confirm(`Skicka om kvitto + nedladdningslänkar till ${customerEmail}?`)) return;
    try {
      const r = await api.post(`/admin/orders/${orderId}/resend-email`);
      alert(`✓ E-post skickad till ${r.data.email} (${r.data.links} länkar)`);
    } catch (e) {
      alert(e?.response?.data?.detail || "E-post kunde inte skickas");
    }
  };

  const adminDownloadPhoto = (orderId, photoId) => {
    const t = encodeURIComponent(getToken() || "");
    window.open(`${API}/admin/orders/${orderId}/download/${photoId}?t=${t}`, "_blank");
  };

  const deletePhotographer = async (p) => {
    const msg =
      `Radera fotograf "${p.name}" (${p.email})?\n\n` +
      `Detta tar permanent bort:\n` +
      `· Kontot\n` +
      `· ${p.photo_count} foto${p.photo_count === 1 ? "" : "n"} med originaler & previews i R2\n\n` +
      `Tidigare ordrar/intäkter sparas för bokföring.\n\n` +
      `Vill du fortsätta?`;
    if (!window.confirm(msg)) return;
    try {
      const r = await api.delete(`/admin/photographers/${p.id}`);
      alert(`Klart. ${r.data.deleted_photos} foton raderades.`);
      refresh();
    } catch (e) {
      alert(e?.response?.data?.detail || "Kunde inte radera fotograf");
    }
  };

  const saveEvent = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      classes: form.classes ? form.classes.split(",").map((s) => s.trim()).filter(Boolean) : [],
      heats: form.heats ? form.heats.split(",").map((s) => s.trim()).filter(Boolean) : [],
    };
    if (editing) {
      await api.patch(`/events/${editing}`, payload);
    } else {
      await api.post("/events", payload);
    }
    setForm(emptyEvent);
    setEditing(null);
    refresh();
  };

  const editEvent = (ev) => {
    setEditing(ev.id);
    setForm({
      name: ev.name,
      date: ev.date,
      location: ev.location,
      series: ev.series || "",
      classes: (ev.classes || []).join(", "),
      heats: (ev.heats || []).join(", "),
      cover_image_url: ev.cover_image_url || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const removeEvent = async (id) => {
    if (!window.confirm("Ta bort eventet?")) return;
    await api.delete(`/events/${id}`);
    refresh();
  };

  const approveEvent = async (id) => {
    await api.post(`/admin/events/${id}/approve`);
    refresh();
  };

  return (
    <div className="App">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <p className="label-overline mb-2">Plattformsadmin</p>
        <h1 className="font-display font-black uppercase text-white text-4xl sm:text-5xl mb-8">Kontrollrum</h1>

        <div className="flex flex-wrap gap-1 border-b border-white/10 mb-8" data-testid="admin-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-4 py-3 font-display uppercase tracking-wider text-sm border-b-2 transition-colors flex items-center gap-2 ${
                  tab === t.id ? "border-brand text-white" : "border-transparent text-white/50 hover:text-white"
                }`}
                data-testid={`admin-tab-${t.id}`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {tab === "stats" && stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-3" data-testid="admin-stats">
            {[
              { l: "Event", v: stats.events },
              { l: "Fotografer", v: stats.photographers },
              { l: "Foton", v: stats.photos },
              { l: "Leads", v: stats.leads },
              { l: "Paid orders", v: stats.orders_paid },
              { l: "Intäkt (kr)", v: stats.revenue_sek?.toFixed(0) },
              { l: "Moms (kr)", v: stats.vat_sek?.toFixed(0) },
              { l: "Sponsorpott (kr)", v: stats.sponsorship_balance?.toFixed(0) },
            ].map((s) => (
              <div key={s.l} className="card-surface p-4">
                <p className="text-3xl font-display font-black text-white">{s.v}</p>
                <p className="text-xs uppercase tracking-wider text-white/50 mt-1">{s.l}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "events" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="space-y-2" data-testid="admin-events-list">
                {events.map((ev) => (
                  <div key={ev.id} className="card-surface p-4 flex items-center gap-3" data-testid={`admin-event-${ev.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-display font-bold uppercase text-white truncate">{ev.name}</p>
                        {ev.status === "pending" && (
                          <span className="bg-yellow-500/20 text-yellow-400 text-xs font-bold uppercase px-2 py-0.5" data-testid={`event-pending-${ev.id}`}>
                            Väntar
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-white/50">
                        {ev.date} · {ev.location} · {ev.photo_count || 0} foton
                        {ev.created_by && ev.created_by !== "admin" && " · skapad av fotograf"}
                      </p>
                    </div>
                    {ev.status === "pending" && (
                      <button
                        onClick={() => approveEvent(ev.id)}
                        className="bg-green-700/40 text-green-400 hover:bg-green-700/60 px-3 py-1.5 text-xs font-bold uppercase"
                        data-testid={`approve-event-${ev.id}`}
                      >
                        Godkänn
                      </button>
                    )}
                    <button onClick={() => editEvent(ev)} className="text-white/60 hover:text-brand p-1" data-testid={`edit-event-${ev.id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => removeEvent(ev.id)} className="text-white/60 hover:text-destructive p-1" data-testid={`delete-event-${ev.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <form onSubmit={saveEvent} className="card-surface p-4 h-fit space-y-3" data-testid="admin-event-form">
              <h3 className="font-display font-bold uppercase text-white">{editing ? "Redigera event" : "Nytt event"}</h3>
              <input required placeholder="Namn" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-field" data-testid="event-form-name" />
              <input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="input-field" data-testid="event-form-date" />
              <input required placeholder="Plats" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="input-field" data-testid="event-form-location" />
              <input placeholder="Serie (frivilligt)" value={form.series} onChange={(e) => setForm({ ...form, series: e.target.value })} className="input-field" />
              <input placeholder="Klasser (kommaseparerat)" value={form.classes} onChange={(e) => setForm({ ...form, classes: e.target.value })} className="input-field" />
              <input placeholder="Heat (kommaseparerat)" value={form.heats} onChange={(e) => setForm({ ...form, heats: e.target.value })} className="input-field" />

              {editing ? (
                <CoverImageUploader
                  eventId={editing}
                  currentUrl={form.cover_image_url}
                  onUploaded={(url) => { setForm({ ...form, cover_image_url: url }); refresh(); }}
                />
              ) : (
                <p className="text-xs text-white/40 italic">
                  Bilder läggs till efter att eventet är skapat. Klicka "Skapa" först, sen "Redigera" för att ladda upp omslagsbild.
                </p>
              )}

              <div className="flex gap-2">
                <button type="submit" className="btn-primary flex-1 flex items-center justify-center gap-1" data-testid="event-form-submit">
                  <Plus className="w-4 h-4" /> {editing ? "Uppdatera" : "Skapa"}
                </button>
                {editing && (
                  <button type="button" onClick={() => { setEditing(null); setForm(emptyEvent); }} className="btn-secondary">×</button>
                )}
              </div>
            </form>
          </div>
        )}

        {tab === "photographers" && (
          <div className="space-y-2" data-testid="admin-photographers">
            {photographers.map((p) => (
              <div key={p.id} className="card-surface p-4 flex items-center gap-4">
                <div className="w-10 h-10 bg-brand/20 flex items-center justify-center font-display font-bold text-brand">
                  {p.name?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-white truncate">{p.name}</p>
                  <p className="text-xs text-white/50 truncate">{p.email}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-white">{p.photo_count} foton</p>
                  <p className="text-xs text-brand">{p.total_earnings_sek?.toFixed(0)} kr</p>
                </div>
                <span className={`text-xs px-2 py-1 uppercase ${p.onboarding_status === "complete" ? "bg-green-700/40 text-green-400" : "bg-yellow-700/40 text-yellow-400"}`}>
                  {p.onboarding_status}
                </span>
                <button
                  onClick={() => deletePhotographer(p)}
                  className="btn-secondary text-xs py-2 px-2 hover:bg-red-900/40 hover:border-red-700/60"
                  title="Radera fotograf och alla deras foton"
                  data-testid={`delete-photographer-${p.id}`}
                >
                  <Trash2 className="w-4 h-4 text-red-400" />
                </button>
              </div>
            ))}
            {photographers.length === 0 && (
              <p className="text-white/40 text-center py-8">Inga fotografer registrerade.</p>
            )}
          </div>
        )}

        {tab === "orders" && (
          <div className="space-y-2" data-testid="admin-orders">
            {orders.length === 0 ? <p className="text-white/40">Inga ordrar.</p> : orders.map((o) => (
              <button
                key={o.id}
                onClick={() => openOrder(o.id)}
                className="card-surface p-4 w-full text-left hover:border-brand/60 transition-colors"
                data-testid={`admin-order-${o.id}`}
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="font-bold text-white">{o.customer_name}</p>
                    <p className="text-xs text-white/50">{o.customer_email} · {new Date(o.created_at).toLocaleString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-display font-bold text-brand">{o.total_amount?.toFixed(0)} kr</p>
                    <span className={`text-xs uppercase px-2 py-0.5 ${o.status === "paid" ? "bg-green-700/40 text-green-400" : "bg-yellow-700/40 text-yellow-400"}`}>{o.status}</span>
                  </div>
                </div>
                <div className="text-xs text-white/50 mt-2 flex flex-wrap items-center gap-3">
                  <span>{o.photo_ids?.length} foton</span>
                  <span>•</span>
                  <span>{o.stripe_charge_mode === "destination" ? "Destination charge" : "Platform charge"}</span>
                  {o.discount > 0 && <><span>•</span><span className="text-green-500">Bundle −{o.discount.toFixed(0)} kr</span></>}
                  {o.payout_type === "mixed" && <><span>•</span><span className="text-brand">Multi-fotograf</span></>}
                </div>
              </button>
            ))}
          </div>
        )}

        {tab === "leads" && (
          <div className="space-y-2" data-testid="admin-leads">
            {leads.length === 0 ? <p className="text-white/40">Inga leads än.</p> : leads.map((l) => (
              <div key={l.id} className="card-surface p-4 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-bold text-white">{l.name}</p>
                  <p className="text-xs text-white/50">{l.email}{l.phone ? ` · ${l.phone}` : ""}</p>
                </div>
                <div className="text-right text-xs">
                  <p className="text-white/70">{l.bib_number_searched ? `Bib #${l.bib_number_searched}` : "—"}</p>
                  <p className="text-white/40">{new Date(l.created_at).toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "sponsorship" && pot && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="admin-sponsorship">
            <div className="lg:col-span-2">
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="card-surface p-4">
                  <p className="text-3xl font-display font-black text-white">{pot.total_credited?.toFixed(0)} kr</p>
                  <p className="text-xs uppercase text-white/50 mt-1">Totalt inkommit</p>
                </div>
                <div className="card-surface p-4">
                  <p className="text-3xl font-display font-black text-white">{pot.total_spent?.toFixed(0)} kr</p>
                  <p className="text-xs uppercase text-white/50 mt-1">Utbetalat</p>
                </div>
                <div className="card-surface p-4 border-brand/40">
                  <p className="text-3xl font-display font-black text-brand">{pot.total_balance?.toFixed(0)} kr</p>
                  <p className="text-xs uppercase text-white/50 mt-1">Kvar</p>
                </div>
              </div>

              <h3 className="label-overline mb-3">Per förare</h3>
              <div className="space-y-2 mb-8">
                {pot.riders.length === 0 ? (
                  <p className="text-white/40">Ingen sponsring registrerad än.</p>
                ) : pot.riders.map((r, i) => (
                  <div key={i} className="card-surface p-4 flex items-center justify-between gap-4">
                    <div>
                      <p className="font-bold text-white">{r.rider_name}{r.rider_bib ? ` · #${r.rider_bib}` : ""}</p>
                      <p className="text-xs text-white/50">
                        In: {r.credited.toFixed(0)} kr · Ut: {r.spent.toFixed(0)} kr
                      </p>
                    </div>
                    <p className={`font-display font-black text-2xl ${r.balance > 0 ? "text-brand" : "text-white/60"}`}>
                      {r.balance.toFixed(0)} kr
                    </p>
                  </div>
                ))}
              </div>

              <h3 className="label-overline mb-3">Senaste utbetalningar</h3>
              <div className="space-y-2">
                {pot.payouts.length === 0 ? (
                  <p className="text-white/40 text-sm">Inga utbetalningar än.</p>
                ) : pot.payouts.slice(0, 20).map((p) => (
                  <div key={p.id} className="card-surface p-3 flex items-center justify-between gap-3 text-sm">
                    <div className="flex-1 min-w-0">
                      <p className="text-white truncate">{p.description}</p>
                      <p className="text-xs text-white/50">
                        {p.rider_name}{p.rider_bib ? ` #${p.rider_bib}` : ""} · {new Date(p.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    {p.receipt_url && (
                      <a href={p.receipt_url} target="_blank" rel="noreferrer" className="text-brand hover:underline text-xs flex items-center gap-1">
                        <Receipt className="w-3 h-3" /> Kvitto
                      </a>
                    )}
                    <p className="font-display font-bold text-white whitespace-nowrap">−{p.amount.toFixed(0)} kr</p>
                  </div>
                ))}
              </div>
            </div>

            <form onSubmit={submitPayout} className="card-surface p-4 h-fit space-y-3" data-testid="sponsorship-payout-form">
              <h3 className="font-display font-bold uppercase text-white">Registrera utbetalning</h3>
              <p className="text-xs text-white/50">T.ex. inköp av däck, bensin, delar från sponsorpotten.</p>
              <input required placeholder="Förarnamn" value={payoutForm.rider_name} onChange={(e) => setPayoutForm({ ...payoutForm, rider_name: e.target.value })} className="input-field" data-testid="sp-form-rider" />
              <input placeholder="Startnr (frivilligt)" value={payoutForm.rider_bib} onChange={(e) => setPayoutForm({ ...payoutForm, rider_bib: e.target.value })} className="input-field" data-testid="sp-form-bib" />
              <input required type="number" step="0.01" placeholder="Belopp (kr)" value={payoutForm.amount} onChange={(e) => setPayoutForm({ ...payoutForm, amount: e.target.value })} className="input-field" data-testid="sp-form-amount" />
              <input required placeholder="Beskrivning (t.ex. Michelin Enduro-däck)" value={payoutForm.description} onChange={(e) => setPayoutForm({ ...payoutForm, description: e.target.value })} className="input-field" data-testid="sp-form-desc" />
              <input placeholder="Kvitto-URL (frivilligt)" value={payoutForm.receipt_url} onChange={(e) => setPayoutForm({ ...payoutForm, receipt_url: e.target.value })} className="input-field" data-testid="sp-form-receipt" />
              <button type="submit" className="btn-primary w-full" data-testid="sp-form-submit">Registrera utbetalning</button>
            </form>
          </div>
        )}
      </div>
      {orderDetail && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center overflow-y-auto p-4 print:bg-white print:p-0"
          onClick={() => setOrderDetail(null)}
          data-testid="order-detail-modal"
        >
          <div
            className="bg-white text-black max-w-4xl w-full my-8 rounded shadow-2xl print:shadow-none print:my-0 print:max-w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-black/10 print:hidden">
              <h2 className="font-display font-bold uppercase text-xl">Order {orderDetail.order.id.slice(0, 8)}</h2>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => resendOrderEmail(orderDetail.order.id, orderDetail.order.customer_email)}
                  className="bg-black text-white px-4 py-2 text-sm font-display uppercase flex items-center gap-1 hover:bg-black/80"
                  data-testid="order-resend-email"
                  title={`Skicka om kvitto till ${orderDetail.order.customer_email}`}
                >
                  <Send className="w-4 h-4" /> Skicka om e-post
                </button>
                <button onClick={() => window.print()} className="bg-[#FF6B2C] text-white px-4 py-2 text-sm font-display uppercase flex items-center gap-1" data-testid="order-print">
                  <Printer className="w-4 h-4" /> Skriv ut
                </button>
                <a
                  href={`/receipt/${orderDetail.order.id}?email=${encodeURIComponent(orderDetail.order.customer_email)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="bg-black/10 text-black px-4 py-2 text-sm font-display uppercase flex items-center gap-1"
                  data-testid="order-public-receipt"
                >
                  <ExternalLink className="w-4 h-4" /> Kundvy
                </a>
                <button onClick={() => setOrderDetail(null)} className="text-black/50 hover:text-black p-2">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <ReceiptLayout order={orderDetail.order} items={orderDetail.items} sellerInfo={orderDetail.platform} />
            <div className="px-8 py-4 border-t border-black/10 text-xs print:hidden">
              <p className="uppercase tracking-wider text-black/50 mb-2 font-bold">Intern information (syns ej på kundkvitto)</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-black/70">
                <div>
                  <p className="text-black/50">Charge mode</p>
                  <p className="font-mono">{orderDetail.order.stripe_charge_mode}</p>
                </div>
                <div>
                  <p className="text-black/50">Session</p>
                  <p className="font-mono break-all">{orderDetail.order.session_id}</p>
                </div>
                <div>
                  <p className="text-black/50">Payout-typ</p>
                  <p>{orderDetail.order.payout_type}</p>
                </div>
                <div>
                  <p className="text-black/50">Antal fotografer</p>
                  <p>{orderDetail.order.photographer_ids?.length || 1}</p>
                </div>
              </div>
              <p className="uppercase tracking-wider text-black/50 mt-4 mb-2 font-bold">Utbetalningar per fotograf</p>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-black/10 text-left">
                    <th className="py-1">Fotograf</th>
                    <th className="py-1">Typ</th>
                    <th className="py-1 text-right">Fotograf får</th>
                    <th className="py-1 text-right">Provision</th>
                    <th className="py-1">Transfer</th>
                  </tr>
                </thead>
                <tbody>
                  {orderDetail.items.map((it, i) => (
                    <tr key={i} className="border-b border-black/5">
                      <td className="py-1">{it.photographer_name}</td>
                      <td className="py-1">{it.photographer_payout_type}</td>
                      <td className="py-1 text-right">{(Number(it.photographer_amount) || 0).toFixed(2)} kr</td>
                      <td className="py-1 text-right">{(Number(it.platform_fee) || 0).toFixed(2)} kr</td>
                      <td className="py-1 font-mono text-[10px]">{it.transfer_status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <p className="uppercase tracking-wider text-black/50 mt-6 mb-2 font-bold">Originalfoton (support-nedladdning)</p>
              <p className="text-[11px] text-black/50 mb-2">Använd om kunden inte kan ladda ner — bilden öppnas i ny flik och kan sparas/mejlas vidare.</p>
              <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-8 gap-2">
                {orderDetail.items.map((it) => (
                  <div key={it.photo_id} className="border border-black/10 p-1">
                    <div className="aspect-square overflow-hidden bg-black/5">
                      {it.photo_thumb_url && (
                        <img src={`${FILE_BASE}${it.photo_thumb_url}`} alt="" className="w-full h-full object-cover" />
                      )}
                    </div>
                    <button
                      onClick={() => adminDownloadPhoto(orderDetail.order.id, it.photo_id)}
                      className="mt-1 w-full bg-black text-white text-[10px] uppercase tracking-wider font-bold py-1 flex items-center justify-center gap-1 hover:bg-black/80"
                      data-testid={`admin-download-${it.photo_id}`}
                      title="Ladda ner original"
                    >
                      <Download className="w-3 h-3" /> Original
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
}

function CoverImageUploader({ eventId, currentUrl, onUploaded }) {
  const fileInput = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      alert("Max 10 MB omslagsbild");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/events/${eventId}/cover-image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded(r.data.cover_image_url);
    } catch (e) {
      alert(e?.response?.data?.detail || "Kunde inte ladda upp");
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Ta bort omslagsbilden?")) return;
    await api.delete(`/events/${eventId}/cover-image`);
    onUploaded("");
  };

  return (
    <div className="space-y-2" data-testid="cover-uploader">
      <p className="label-overline">Omslagsbild</p>
      <div className="aspect-[16/9] bg-[#1a1a1a] border border-white/10 overflow-hidden">
        {currentUrl ? (
          <img
            src={resolveImageUrl(currentUrl)}
            alt="Cover"
            className="w-full h-full object-cover"
            data-testid="cover-preview"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="w-10 h-10 text-white/20" />
          </div>
        )}
      </div>
      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
        data-testid="cover-input"
      />
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          disabled={uploading}
          className="btn-secondary text-sm py-2 flex-1 inline-flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="cover-upload-btn"
        >
          <Camera className="w-4 h-4" />
          {uploading ? "Laddar upp..." : currentUrl ? "Byt bild" : "Ladda upp"}
        </button>
        {currentUrl && (
          <button
            type="button"
            onClick={remove}
            className="btn-secondary text-sm py-2 px-3 hover:bg-red-900/40"
            data-testid="cover-remove-btn"
          >
            <Trash2 className="w-4 h-4 text-red-400" />
          </button>
        )}
      </div>
      <p className="text-[10px] text-white/40">JPG/PNG · max 10 MB · skalas till 1600px</p>
    </div>
  );
}
